// src/features/dashboard/components/TopApplications.tsx

import { useMemo } from 'react';

import {
  useTopApplications,
} from '../hooks/Employee/useTopApplications';

import {
  useDashboardDate,
} from '../context/DashboardDateContext';

interface TopApplicationsProps {}

const getCategoryStyles = (
  category?: string,
) => {
  switch (category?.toLowerCase()) {
    case 'productive':
      return {
        badge: 'bg-green-100 text-green-700',
        progress: 'bg-green-500',
        icon: 'bg-green-500',
      };

    case 'idle':
      return {
        badge: 'bg-yellow-100 text-yellow-700',
        progress: 'bg-yellow-500',
        icon: 'bg-yellow-500',
      };

    case 'neutral':
      return {
        badge: 'bg-blue-100 text-blue-700',
        progress: 'bg-blue-500',
        icon: 'bg-blue-500',
      };

    default:
      return {
        badge: 'bg-red-100 text-red-700',
        progress: 'bg-red-500',
        icon: 'bg-red-500',
      };
  }
};

const formatHours = (
  hours?: number,
) => {
  const safeHours =
    Number(hours) || 0;

  const totalMinutes =
    Math.round(
      safeHours * 60,
    );

  const h =
    Math.floor(
      totalMinutes / 60,
    );

  const m =
    totalMinutes % 60;

  return `${h}h ${m}m`;
};

const normalizeDate = (
  date: Date | string,
): Date | null => {
  const parsedDate =
    date instanceof Date
      ? date
      : new Date(date);

  if (
    Number.isNaN(
      parsedDate.getTime(),
    )
  ) {
    return null;
  }

  return new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
};

const getDateLabel = (
  selectedDate?: Date | string,
) => {
  if (!selectedDate) {
    return 'Today';
  }

  const selected =
    normalizeDate(
      selectedDate,
    );

  if (!selected) {
    return 'Today';
  }

  const today =
    normalizeDate(
      new Date(),
    );

  if (!today) {
    return 'Today';
  }

  const yesterday =
    new Date(today);

  yesterday.setDate(
    yesterday.getDate() - 1,
  );

  /*
   * Today
   */
  if (
    selected.getTime() ===
    today.getTime()
  ) {
    return 'Today';
  }

  /*
   * Yesterday
   */
  if (
    selected.getTime() ===
    yesterday.getTime()
  ) {
    return 'Yesterday';
  }

  /*
   * Other date
   */
  return selected.toLocaleDateString(
    'en-IN',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  );
};

const TopApplications = (
  _props: TopApplicationsProps,
) => {
  /*
   * Dashboard date comes from
   * DashboardDateContext.
   */
  const {
    fromDate,
  } = useDashboardDate();

  /*
   * Dynamic date label.
   *
   * Today     -> Today
   * Yesterday -> Yesterday
   * Other     -> 15 Aug 2026
   */
  const dateLabel = useMemo(
    () =>
      getDateLabel(
        fromDate,
      ),
    [fromDate],
  );

  /*
   * IMPORTANT:
   *
   * Do NOT pass fromDate here.
   *
   * useTopApplications already
   * gets fromDate/toDate from
   * DashboardDateContext internally.
   */
  const {
    data,
    loading,
  } = useTopApplications();

  /*
   * Always use a safe array.
   */
  const applications =
    data?.applications ?? [];

  /*
   * Loading
   */
  if (loading) {
    return (
      <div
        className="
          rounded-[14px]
          border
          border-[#D0D5DD]
          bg-white
          px-[16px]
          py-[14px]
        "
      >
        <div
          className="
            mb-[14px]
            flex
            items-center
            gap-[8px]
          "
        >
          <h2
            className="
              text-[12px]
              font-semibold
              text-[#002B5B]
            "
          >
            Top Applications · {dateLabel}
          </h2>

          {/* Hardcoded ranking label */}
          <span
            className="
              rounded-full
              bg-[#EAF2FF]
              px-[8px]
              py-[3px]
              text-[9px]
              font-medium
              text-[#1565D8]
              whitespace-nowrap
            "
          >
            Ranked by active time
          </span>
        </div>

        <div
          className="
            flex
            min-h-[100px]
            items-center
            justify-center
            text-[11px]
            text-slate-400
          "
        >
          Loading application usage...
        </div>
      </div>
    );
  }

  /*
   * Empty state
   */
  if (
    applications.length === 0
  ) {
    return (
      <div
        className="
          rounded-[14px]
          border
          border-[#D0D5DD]
          bg-white
          px-[16px]
          py-[14px]
        "
      >
        {/* Header */}
        <div
          className="
            mb-[14px]
            flex
            items-center
            gap-[8px]
          "
        >
          <h2
            className="
              text-[12px]
              font-semibold
              text-[#002B5B]
            "
          >
            Top Applications · {dateLabel}
          </h2>

          {/* Hardcoded ranking label */}
          <span
            className="
              rounded-full
              bg-[#EAF2FF]
              px-[8px]
              py-[3px]
              text-[9px]
              font-medium
              text-[#1565D8]
              whitespace-nowrap
            "
          >
            Ranked by active time
          </span>
        </div>

        {/* Empty Message */}
        <div
          className="
            flex
            min-h-[100px]
            items-center
            justify-center
            text-center
            text-[11px]
            text-slate-400
          "
        >
          {dateLabel === 'Today' ||
          dateLabel === 'Yesterday'
            ? 'No application usage found'
            : `No application usage found for ${dateLabel}`}
        </div>
      </div>
    );
  }

  /*
   * Data available
   */
  return (
    <div
      className="
        rounded-[14px]
        border
        border-[#D0D5DD]
        bg-white
        px-[16px]
        py-[14px]
      "
    >
      {/* Header */}
      <div
        className="
          mb-[14px]
          flex
          items-center
          gap-[8px]
        "
      >
        <h2
          className="
            text-[12px]
            font-semibold
            text-[#002B5B]
          "
        >
          Top Applications · {dateLabel}
        </h2>

        {/* Hardcoded blue ranking pill */}
        <span
          className="
            rounded-full
            bg-[#EAF2FF]
            px-[8px]
            py-[3px]
            text-[9px]
            font-medium
            text-[#1565D8]
            whitespace-nowrap
          "
        >
          Ranked by active time
        </span>
      </div>

      {/* Applications */}
      <div className="space-y-[10px]">
        {applications.map(
          (
            item,
            index,
          ) => {
            const styles =
              getCategoryStyles(
                item.categoryType,
              );

            const applicationName =
              item.applicationName ||
              'Unknown Application';

            const usagePercentage =
              Math.min(
                Math.max(
                  Number(
                    item.usagePercentage,
                  ) || 0,
                  0,
                ),
                100,
              );

            return (
              <div
                key={`${applicationName}-${index}`}
              >
                <div
                  className="
                    flex
                    items-center
                    gap-[10px]
                  "
                >
                  {/* Application Icon */}
                  <div
                    className={`
                      flex
                      h-[30px]
                      w-[30px]
                      shrink-0
                      items-center
                      justify-center
                      rounded-[8px]
                      text-[12px]
                      font-semibold
                      text-white
                      ${styles.icon}
                    `}
                  >
                    {applicationName
                      .charAt(0)
                      .toUpperCase()}
                  </div>

                  {/* Application Details */}
                  <div
                    className="
                      min-w-0
                      flex-1
                    "
                  >
                    <div
                      className="
                        mb-[5px]
                        flex
                        items-center
                        justify-between
                        gap-[10px]
                      "
                    >
                      <h3
                        className="
                          truncate
                          text-[11px]
                          font-medium
                          text-[#101828]
                        "
                      >
                        {applicationName}
                      </h3>

                      <span
                        className="
                          shrink-0
                          text-[10px]
                          text-[#101828]
                        "
                      >
                        {formatHours(
                          item.durationHours,
                        )}
                      </span>
                    </div>

                    <div
                      className="
                        flex
                        items-center
                        gap-[10px]
                      "
                    >
                      {/* Progress */}
                      <div
                        className="
                          h-[6px]
                          flex-1
                          overflow-hidden
                          rounded-full
                          bg-gray-200
                        "
                      >
                        <div
                          className={`
                            h-full
                            rounded-full
                            ${styles.progress}
                          `}
                          style={{
                            width: `${usagePercentage}%`,
                          }}
                        />
                      </div>

                      {/* Category */}
                      <div
                        className={`
                          shrink-0
                          rounded-[4px]
                          px-[8px]
                          py-[3px]
                          text-[9px]
                          font-semibold
                          ${styles.badge}
                        `}
                      >
                        {item.categoryType ||
                          'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index <
                  applications.length - 1 && (
                  <div
                    className="
                      mt-[10px]
                      border-t
                      border-[#EAECF0]
                    "
                  />
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default TopApplications;